// Single source of truth for the 8 canonical module display names and their
// submodule lists. Renaming a module or adding a submodule here (via the
// Form Registry's Module Scope panel) is meant to propagate to the Sidebar,
// the Executive dashboard's module grid, RBAC role-assignment screens, and
// each live module page's tab row — see useModuleCatalog()/useModuleTabs().
import { useEffect, useMemo, useState } from 'react';
import { readRegistryCollection, writeRegistryCollection } from '../component/Admin/data/registryStore';

export const MODULE_CATALOG_STORAGE_KEY = 'module-catalog';

// Canonical defaults — mirrors what is live in Sidebar.js / each module
// page's hardcoded tabs today, so wiring a page onto the catalog is a no-op
// until an admin actually renames something or adds a submodule.
export const DEFAULT_MODULE_CATALOG = [
  {
    id: 'livestock-population',
    name: 'Livestock Population',
    path: '/livestock-population',
    submodules: [
      { key: 'livestock-population-record', label: 'Livestock Population Records' },
    ],
  },
  {
    id: 'markets-trade',
    name: 'Markets & Trade Intelligence',
    path: '/markets-trade',
    submodules: [
      { key: 'market-trade', label: 'Market Registry' },
      { key: 'market-prices', label: 'Market Prices' },
      { key: 'border-crossing-registry', label: 'Border Crossing Registry' },
      { key: 'veterinary-certificate-registry', label: 'Veterinary Certificates Registry' },
    ],
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure & Services',
    path: '/infrastructure',
    submodules: [
      { key: 'core', label: 'Facility Core Registry' },
      { key: 'status', label: 'Facility Status Update' },
    ],
  },
  {
    id: 'workforce-institutional',
    name: 'Workforce & Institutional Actors',
    path: '/workforce-institutional',
    submodules: [
      { key: 'actors', label: 'Livestock Actors' },
      { key: 'deployment', label: 'Workforce Deployment Records' },
      { key: 'coop', label: 'Cooperatives / Organisation' },
    ],
  },
  {
    id: 'planning-reporting',
    name: 'Planning & Reporting (PAR)',
    path: '/planning-reporting',
    submodules: [
      { key: 'workplans', label: 'Workplan & Activities' },
      { key: 'reports', label: 'Activity Reports' },
      { key: 'validation', label: 'PAR Validation History' },
    ],
  },
  {
    id: 'conflicts-dispute',
    name: 'Conflicts & Dispute Records',
    path: '/conflicts-dispute',
    submodules: [
      { key: 'records', label: 'Conflict Records' },
      { key: 'committees', label: 'Conflict Committees' },
      { key: 'routes', label: 'Transhumance Routes' },
    ],
  },
  {
    id: 'animal-identification',
    name: 'Animal Identification',
    path: '/animal-identification',
    submodules: [
      { key: 'registrations', label: 'Animal ID Registrations' },
    ],
  },
  {
    id: 'animal-health',
    name: 'Animal Health',
    path: '/animal-health',
    submodules: [
      { key: 'outbreak', label: 'Outbreak Records' },
      { key: 'vaccination', label: 'Vaccination Records' },
      { key: 'mortality', label: 'Mortality Records' },
      { key: 'zoonotic', label: 'Zoonotic & AMR Flags' },
    ],
  },
];

function cloneCatalog(catalog) {
  return catalog.map((module) => ({ ...module, submodules: (module.submodules ?? []).map((sm) => ({ ...sm })) }));
}

function readOverrides() {
  const raw = readRegistryCollection(MODULE_CATALOG_STORAGE_KEY);
  return Array.isArray(raw) ? raw : [];
}

// Persisted rows are *overrides* keyed by module id — only name/submodules an
// admin actually changed. path/order always come from the code defaults, so
// a code change is never shadowed by a stale browser cache (same pattern as
// the Form Registry's own storage split).
function buildCatalog() {
  const overridesById = new Map(readOverrides().map((row) => [row.id, row]));
  return cloneCatalog(DEFAULT_MODULE_CATALOG).map((module) => {
    const override = overridesById.get(module.id);
    if (!override) return module;
    return {
      ...module,
      name: (typeof override.name === 'string' && override.name.trim()) ? override.name.trim() : module.name,
      submodules: Array.isArray(override.submodules) && override.submodules.length > 0
        ? override.submodules.map((sm) => ({ ...sm }))
        : module.submodules,
    };
  });
}

function persistOverrides(catalog) {
  const overrides = catalog
    .map((module) => {
      const base = DEFAULT_MODULE_CATALOG.find((item) => item.id === module.id);
      if (!base) return null;
      const nameChanged = module.name !== base.name;
      const submodulesChanged = JSON.stringify(module.submodules) !== JSON.stringify(base.submodules);
      if (!nameChanged && !submodulesChanged) return null;
      return { id: module.id, name: module.name, submodules: module.submodules };
    })
    .filter(Boolean);
  writeRegistryCollection(MODULE_CATALOG_STORAGE_KEY, overrides);
}

function slugifySubmoduleKey(value, existing = []) {
  const base = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'submodule';
  const existingKeys = new Set(existing.map((sm) => sm.key));
  if (!existingKeys.has(base)) return base;
  let i = 2;
  let candidate = `${base}-${i}`;
  while (existingKeys.has(candidate)) {
    i += 1;
    candidate = `${base}-${i}`;
  }
  return candidate;
}

// Non-reactive snapshot reads — safe to call from plain (non-component)
// modules that are evaluated once at import time (coreModulesConfig.js,
// ModuleScopeField.jsx). They resolve on page load; live edits made
// elsewhere in the same session need a refresh to show up in those spots.
export function getModuleName(moduleId) {
  return buildCatalog().find((module) => module.id === moduleId)?.name ?? moduleId;
}

export function getModuleSubmodules(moduleId) {
  return buildCatalog().find((module) => module.id === moduleId)?.submodules ?? [];
}

// Live, reactive catalog for React components — updates immediately across
// every mounted consumer when a module is renamed or a submodule is added.
export function useModuleCatalog() {
  const [catalog, setCatalog] = useState(() => buildCatalog());

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleUpdate = (event) => {
      if (event?.detail?.key !== MODULE_CATALOG_STORAGE_KEY) return;
      setCatalog(buildCatalog());
    };
    window.addEventListener('nlims:registry-updated', handleUpdate);
    return () => window.removeEventListener('nlims:registry-updated', handleUpdate);
  }, []);

  const renameModule = (moduleId, nextName) => {
    const trimmed = String(nextName ?? '').trim();
    if (!trimmed) return;
    setCatalog((prev) => {
      const next = prev.map((module) => (module.id === moduleId ? { ...module, name: trimmed } : module));
      persistOverrides(next);
      return next;
    });
  };

  const addSubmodule = (moduleId, { key, label } = {}) => {
    const trimmedLabel = String(label ?? '').trim();
    if (!trimmedLabel) return null;

    const currentModule = catalog.find((module) => module.id === moduleId);
    if (!currentModule) return null;
    const nextKey = slugifySubmoduleKey(key || trimmedLabel, currentModule.submodules);

    setCatalog((prev) => {
      const next = prev.map((module) => (
        module.id === moduleId
          ? { ...module, submodules: [...module.submodules, { key: nextKey, label: trimmedLabel }] }
          : module
      ));
      persistOverrides(next);
      return next;
    });

    return nextKey;
  };

  return { catalog, renameModule, addSubmodule };
}

// For a live module page: merges the catalog's submodule list into a page's
// existing local tab definitions (which carry role rules, component
// references, etc. that the catalog knows nothing about). Renaming a
// submodule in the catalog relabels the matching local tab; a brand-new
// catalog submodule that has no local counterpart is appended, flagged
// `isCatalogOnly` so the page can render a placeholder for it instead of a
// real (not-yet-built) data-capture view.
export function useModuleTabs(moduleId, localTabs) {
  const { catalog } = useModuleCatalog();
  return useMemo(() => {
    const submodules = catalog.find((module) => module.id === moduleId)?.submodules ?? [];
    const labelByKey = new Map(submodules.map((sm) => [sm.key, sm.label]));
    const knownKeys = new Set(localTabs.map((tab) => tab.key));
    const merged = localTabs.map((tab) => ({ ...tab, label: labelByKey.get(tab.key) ?? tab.label }));
    const extra = submodules
      .filter((sm) => !knownKeys.has(sm.key))
      .map((sm) => ({ key: sm.key, label: sm.label, isCatalogOnly: true }));
    return [...merged, ...extra];
  }, [catalog, moduleId, localTabs]);
}
